"use client";

import { cn, sectionBackgroundColors, variantBackgroundColors } from "@/lib/utils";
import React, { useRef, useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import Image from '@/components/ui/Image';
import { Button } from "@/components/ui/button";
import { sectionVariants } from "@/types";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import Link from "next/link";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "../ui/input";
import { BorderBeam } from "@/components/magicui/border-beam";

interface PointsCalculatorProps {
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;   
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  attributes?: React.HTMLProps<HTMLElement>;
  calculatorItems?: {
    tabTitle: string;
    conversionRate: number;
    conversionRateUnit: string;
    image: string;
    imageAltText: string;
    title: string;
    content: string;
    disclaimer: string;
  }[];
}

const PointsCalculator = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Points Calculator",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  attributes,
  calculatorItems,
}: PointsCalculatorProps) => {

  const [current, setCurrent] = useState(calculatorItems?.[0]?.tabTitle ?? "");
  const [inputValues, setInputValues] = useState<{ [key: string]: number }>({});
  const tabRefs = useRef<HTMLButtonElement[]>([]);

  const cardBackgroundColor = variantBackgroundColors(darkMode, variant);
  const tabBackgroundColor = variantBackgroundColors(darkMode, variant);
  const tabActiveBackgroundColor = sectionBackgroundColors(darkMode, variant);

  const handleInputChange = (tabTitle: string, value: string) => {
    // Only allow up to 9 digits
    if (value.length > 9) {
      return; // Don't update if more than 9 digits
    }
    
    const numValue = parseFloat(value) || 0;
    setInputValues(prev => ({
      ...prev,
      [tabTitle]: numValue
    }));
  };

  const calculatePoints = (tabTitle: string) => {
    const inputValue = inputValues[tabTitle] || 0;
    const currentItem = calculatorItems?.find(item => item.tabTitle === tabTitle);
    const conversionRate = currentItem?.conversionRate || 0;
    const result = Math.floor(inputValue * conversionRate);
    
    return result.toLocaleString();
  };

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="points-calculator"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        <div className="mb-8 md:mb-16 flex flex-col text-center">
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="mt-6 paragraph-lg text-muted-foreground max-w-2xl mx-auto">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div>
              <Button asChild variant={buttonStyle} size="lg" className="mt-10 w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="mt-2 paragraph-sm text-muted-foreground text-center">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="items-center justify-between mb-8 md:mb-16 flex">
          <Tabs
            value={current} 
            onValueChange={setCurrent}
            className="justify-center flex m-auto"
          >
            <TabsList className={cn(tabBackgroundColor, "relative h-16 p-1 gap-0 mx-auto rounded-full")}>
              {calculatorItems?.map((item, idx) => (
                <TabsTrigger
                  key={idx}
                  ref={(el) => {
                    tabRefs.current[idx] = el as HTMLButtonElement;
                  }}
                  value={item.tabTitle ?? ""}
                  className={cn(
                    "paragraph-base px-7 transition-all duration-700 ease-out rounded-full cursor-pointer",
                    "data-[state=active]:shadow-none data-[state=active]:font-bold",
                    tabActiveBackgroundColor === "bg-background" && "data-[state=active]:bg-background",
                    tabActiveBackgroundColor === "bg-muted" && "data-[state=active]:bg-muted"
                  )}
                >
                  {item.tabTitle}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className={cn(cardBackgroundColor, "rounded-2xl relative overflow-hidden")}>
          {calculatorItems && calculatorItems.map((item, index) => ( 
            <div
              key={index}
              className={`${current === item.tabTitle ? "block" : "hidden"}`}
            >
              <div
                tabIndex={-1}
                className="flex flex-col px-6 py-10 md:p-10 gap-10"
              >
                <div className="flex flex-col lg:flex-row gap-6 items-center text-center md:text-left md:items-start lg:items-center">
                  {item.image && (
                    <div className="aspect-[122/111] rounded-lg">
                      <Image
                        src={item.image}
                        alt={item.imageAltText ?? ''}
                        title={item.imageAltText ?? ''}
                        className="rounded-lg object-contain aspect-[122/111]"
                        width={122}
                        height={111}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-4 flex-1">
                    {item.title && (
                      <h4>
                        {parse(item.title)}
                      </h4>
                    )}
                    {item.content && (
                      <div className="paragraph-lg text-foreground">
                        {parse(item.content)}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-6 md:flex-row items-center">
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground !text-[20px]">
                        attach_money
                      </span>
                     <Input
                       className="pl-9"
                       type="number"
                       placeholder=""
                       value={inputValues[item.tabTitle] || ''}
                       onChange={(e) => handleInputChange(item.tabTitle, e.target.value)}
                     />
                    </div>

                    <span className="material-symbols-outlined text-foreground !text-[20px]">
                      equal
                    </span>

                    <div className="flex w-[170px] justify-center md:justify-start">
                      <div className="flex flex-col gap-2 items-center justify-center">
                        <h4>{calculatePoints(item.tabTitle)}</h4>
                        <span className="paragraph-lg text-muted-foreground">{item.conversionRateUnit}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {item.disclaimer && (
                  <div className="paragraph-sm text-muted-foreground text-center md:text-left">
                    {parse(item.disclaimer)}
                  </div>
                )}
                  
              </div>
            </div>
          ))}
          <BorderBeam duration={8} size={300} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { PointsCalculator };



